<script setup lang="ts">
import AppTextarea from '@/shared/ui/AppTextarea/index';

import { modifyDate } from '@/shared/utils/modifyTime';
import { buildFullName } from '@/shared/utils/buildFullName';
import { REPLY_MAX_LENGTH } from '@/pages/journalArticle/lib/useJournalArticleConstants';

import type { ArticleCommentView } from '@/pages/journalArticle/model/types/articleComments';

const props = defineProps<{
  comment: ArticleCommentView;
  isShowReplyField: boolean;
}>();

const emit = defineEmits<{
  (e: 'send', text: string): void;
  (e: 'cancel'): void;
}>();

const isMobile: boolean = useIsMobile();
const replyValue: Ref<string> = ref(props.comment.review || '');

const authorName: ComputedRef<string> = computed(() => {
  const target = props.comment.target;
  return target?.lawyer?.company?.name ?? buildFullName(target);
});

const sendHandler = (): void => {
  const isChanged = replyValue.value && replyValue.value !== props.comment.review;
  if (isChanged) {
    emit('send', replyValue.value);
  }
  emit('cancel');
};
</script>

<template>
  <div
    :class="['article-comment-reply', { _mobile: isMobile }]"
    itemscope
    itemtype="https://schema.org/Comment"
  >
    <div v-if="isShowReplyField" class="article-comment-reply__textarea-wrapper">
      <AppTextarea
        v-model="replyValue"
        class="article-comment-reply__textarea"
        placeholder="Ваш ответ ..."
        :rows="isMobile ? 11 : 5"
        :max-length="REPLY_MAX_LENGTH"
      />
      <div class="article-comment-reply__buttons-wrapper">
        <button
          class="article-comment-reply__button _cancel"
          type="button"
          aria-label="Отменить ответ"
          @click.prevent="emit('cancel')"
        >
          Отменить
        </button>
        <button
          class="article-comment-reply__button _reply"
          type="button"
          aria-label="Отправить ответ"
          @click.prevent="sendHandler"
        >
          <AppIcon name="CommentsArrow" />
        </button>
      </div>
    </div>
    <div v-else-if="comment.review" class="article-comment-reply__comment">
      <div
        class="article-comment-reply__info-wrapper"
        itemprop="author"
        itemscope
        itemtype="https://schema.org/Person"
      >
        <NuxtImg
          v-if="comment.target?.avatar"
          :src="comment.target.avatar"
          loading="lazy"
          class="article-comment-reply__image"
          itemprop="image"
          :alt="comment.target?.name ?? 'аватар автора комментария'"
          width="40"
          height="40"
          densities="x1 x2"
        />
        <div class="article-comment-reply__info">
          <div class="article-comment-reply__author">
            <span itemprop="name">{{ authorName }}</span>
          </div>
          <div class="article-comment-reply__date">
            Ответ автора:
            <span class="article-comment-reply__date-value" itemprop="datePublished">
              {{ modifyDate(comment.updatedAt) }}
            </span>
          </div>
        </div>
      </div>
      <div class="article-comment-reply__text" itemprop="text">
        {{ comment.review }}
      </div>
    </div>
  </div>
</template>

<style lang="less">
.article-comment-reply {
  &__textarea-wrapper {
    position: relative;
  }

  &__textarea .app-textarea__field {
    min-height: 100px;
    background: @color-black-haze;
    border: 0.5px solid @color-silver-chalice;
    padding: @size-x4 60px @size-x4 @size-x4;

    &::placeholder {
      color: @color-silver-chalice;
    }
  }

  &__buttons-wrapper {
    display: flex;
    align-items: center;
    position: absolute;
    bottom: 10px;
    right: 10px;
  }

  &__button {
    .button-reset();

    font-size: 10px;
    line-height: 13px;
    font-weight: 600;

    &._reply {
      margin-left: @size-x5;
    }

    &._cancel {
      font-size: @size-x3;
    }
  }

  &__comment {
    background: @color-black-haze;
    border-radius: @main-size;
    padding: @size-x4;
  }

  &__image {
    max-width: @size-x10;
    width: @size-x10;
    height: @size-x10;
    object-fit: cover;
    border: 0.5px solid @color-silver-chalice;
    border-radius: @main-size;
    margin-right: 10px;
  }

  &__info-wrapper {
    display: flex;
    align-items: center;
  }

  &__author {
    font-size: 14px;
    line-height: 18px;
    font-weight: 600;
    margin-bottom: 2px;
  }

  &__date {
    font-size: @size-x3;
    line-height: @size-x4;
    font-weight: 500;
    color: @color-dove-gray;
  }

  &__date-value {
    color: @color-elephant;
  }

  &__text {
    font-size: 14px;
    line-height: 18px;
    font-weight: 500;
    margin-top: @size-x5;
    margin-left: 50px;
    word-break: break-word;
  }

  &._mobile &__info-wrapper {
    align-items: flex-start;
  }

  &._mobile &__textarea .app-textarea__field {
    font-size: @size-x3;
    line-height: @size-x4;
    padding: @size-x3 50px @size-x3 @size-x3;
  }

  &._mobile &__text {
    margin-top: @size-x3;
  }
}
</style>
